/**
 * ContingencyDeadlineReviewNotice — the journey's neutral notice for an offer
 * signed before deadlines were required, with a selected contingency that has
 * no deadline (legal review §9.8b item 3 [ATTY]).
 *
 * The journey payload (`GET /api/journey/{id}`) carries
 * `contingency_deadline_review`: null, or `{code: 'contingency_deadline_missing',
 * attorney_attention: true, contingencies: [keys], fields: ['<key>_days']}`.
 * The contract is ambiguous on those deadlines, so nothing is computed or
 * shown as "treated as satisfied"; both parties see this notice instead.
 *
 * Copy: `WI.transaction.contingency_deadline_missing` (copy file §10). The
 * server flags registry states (WI) only.
 */
import React from 'react';
import { copyText } from '../SellerDashboard/paperworkCopy';
import { getContingencyLabel } from '../OfferManagement/contingencyCatalog';
import styles from './ContingencyDeadlineReviewNotice.module.css';

export const CONTINGENCY_DEADLINE_MISSING_CODE = 'contingency_deadline_missing';

/** The notice text for a review, or null when there is nothing to say. */
export function contingencyDeadlineReviewText(review) {
  if (!review || review.code !== CONTINGENCY_DEADLINE_MISSING_CODE) return null;
  const keys = Array.isArray(review.contingencies) ? review.contingencies : [];
  if (keys.length === 0) return null;
  const labels = keys.map((key) => getContingencyLabel(key)).filter(Boolean);
  return copyText('WI.transaction.contingency_deadline_missing', { contingencies: labels.join(', ') });
}

export default function ContingencyDeadlineReviewNotice({ review }) {
  const text = contingencyDeadlineReviewText(review);
  if (!text) return null;
  return (
    <section
      className={styles.notice}
      role="note"
      aria-label="Contingencies without a deadline"
      data-testid="contingency-deadline-review"
      data-attorney-attention={review.attorney_attention ? 'true' : undefined}
    >
      <span className={styles.icon} aria-hidden="true">i</span>
      <p className={styles.text}>{text}</p>
    </section>
  );
}
